import React, { useState } from 'react';
import { Paper, Title, Text, Button, Group, Badge, Stack, Alert } from '@mantine/core';
import { IconCheck, IconAlertCircle, IconEdit, IconMail, IconLock, IconEye, IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { resendConfirmationEmail } from '../../services/emailService';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveSeason, isRegistrationOpenForUser, isSeasonPastForUser, usesCart, CONTACT_EMAIL } from '../../config/seasons';
import EntriesSummary from './EntriesSummary';

/**
 * RegistrationCard Component
 * Displays registration status in different states
 */
const RegistrationCard = ({ registration, onDelete }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sendingEmail, setSendingEmail] = useState(false);

  // The season this registration belongs to (falls back to active season for
  // the "no registration" empty state). Late-access overrides let approved
  // users see "Continue Registration" past the season close date.
  const seasonYear = registration?.competition_year || getActiveSeason().year;
  const seasonOpen = isRegistrationOpenForUser(seasonYear, user?.email);

  /**
   * Handle re-sending confirmation email
   */
  const handleResendEmail = async () => {
    if (!registration) return;

    try {
      setSendingEmail(true);

      await resendConfirmationEmail(registration.id);

      notifications.show({
        title: 'Email Sent',
        message: 'Confirmation email has been resent to your inbox',
        color: 'green'
      });
    } catch (error) {
      console.error('Error re-sending email:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to send confirmation email. Please try again.',
        color: 'red'
      });
    } finally {
      setSendingEmail(false);
    }
  };

  /**
   * No Registration State
   */
  if (!registration) {
    return (
      <Paper
        radius="md"
        p="xl"
        withBorder
        style={{
          borderTop: '4px solid var(--primary-gold)',
          textAlign: 'center'
        }}
      >
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
          🎭
        </div>
        <Title order={3} size="h4" mb="md" style={{ color: 'var(--charcoal)' }}>
          Competition Registration
        </Title>
        {seasonOpen ? (
          <>
            <Text size="sm" color="dimmed" mb="xl">
              You haven't started your registration yet.
            </Text>
            <Button
              size="md"
              onClick={() => navigate('/register/individual/step-1')}
              style={{
                background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))'
              }}
            >
              Register for Prix du Canada {seasonYear}
            </Button>
          </>
        ) : isSeasonPastForUser(seasonYear, user?.email) ? (
          <Text size="sm" color="dimmed" mb="md">
            Prix du Canada {seasonYear} has wrapped — thank you to everyone who danced with us.
            Registration for the next season will be announced soon. Questions?{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--primary-gold)', fontWeight: 600 }}>
              {CONTACT_EMAIL}
            </a>
          </Text>
        ) : (
          <Text size="sm" color="dimmed" mb="md">
            Registration for Prix du Canada {seasonYear} is closed. Stay tuned for
            the next season — questions?{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--primary-gold)', fontWeight: 600 }}>
              {CONTACT_EMAIL}
            </a>
          </Text>
        )}
      </Paper>
    );
  }

  /**
   * Draft/Incomplete Registration State
   */
  if (registration.status === 'draft' || registration.status === 'pending_payment') {
    return (
      <Paper
        radius="md"
        p="xl"
        withBorder
        style={{
          borderTop: '4px solid var(--deep-gold)'
        }}
      >
        <Group justify="space-between" mb="md">
          <Group>
            <IconAlertCircle size={24} style={{ color: 'var(--deep-gold)' }} />
            <Title order={3} size="h4" style={{ color: 'var(--charcoal)' }}>
              Registration in Progress
            </Title>
          </Group>
          <Badge color="yellow" size="lg">Incomplete</Badge>
        </Group>

        <Stack spacing="sm" mb="xl">
          <Group justify="space-between">
            <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>Started:</Text>
            <Text size="sm" weight={600}>
              {new Date(registration.created_at).toLocaleDateString()}
            </Text>
          </Group>

          <Group justify="space-between">
            <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>Competition:</Text>
            <Text size="sm" weight={600}>Prix du Canada {registration.competition_year}</Text>
          </Group>

          <Group justify="space-between">
            <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>Last saved:</Text>
            <Text size="sm" weight={600}>Step {registration.current_step} of 4</Text>
          </Group>

          <Group justify="space-between">
            <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>Status:</Text>
            <Badge color="orange" variant="light">
              {registration.status === 'pending_payment' ? 'Awaiting Payment' : 'Draft'}
            </Badge>
          </Group>
        </Stack>

        <Group>
          {seasonOpen ? (
            <Button
              flex={1}
              onClick={() => navigate(`/register/individual/step-${registration.current_step}`)}
              style={{
                background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))'
              }}
            >
              Continue Registration
            </Button>
          ) : (
            <Alert icon={<IconLock size={16} />} color="yellow" variant="light" style={{ flex: 1 }}>
              <Text size="sm">
                Registration for {seasonYear} has closed. We hope to see you next season — in the meantime, come cheer this year's dancers on! Questions? Reach us at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--deep-gold)', fontWeight: 600 }}>
                  {CONTACT_EMAIL}
                </a>.
              </Text>
            </Alert>
          )}
        </Group>
      </Paper>
    );
  }

  /**
   * Complete Registration State
   */
  if (registration.status === 'complete') {
    return (
      <Paper
        radius="md"
        p="xl"
        withBorder
        style={{
          borderTop: '4px solid #4caf50'
        }}
      >
        <Group justify="space-between" mb="md">
          <Group>
            <IconCheck size={24} style={{ color: '#4caf50' }} />
            <Title order={3} size="h4" style={{ color: 'var(--charcoal)' }}>
              Registration Complete
            </Title>
          </Group>
          <Badge color="green" size="lg">Confirmed</Badge>
        </Group>

        <Stack spacing="sm" mb="xl">
          <Group justify="space-between">
            <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>Registration ID:</Text>
            <Text size="sm" weight={600} style={{ fontFamily: 'monospace' }}>
              {registration.registration_number || `PDC${registration.competition_year}-${registration.id.substring(0, 8).toUpperCase()}`}
            </Text>
          </Group>

          <Group justify="space-between">
            <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>Submitted:</Text>
            <Text size="sm" weight={600}>
              {new Date(registration.submitted_at || registration.created_at).toLocaleDateString()}
            </Text>
          </Group>

          <Group justify="space-between">
            <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>Competition:</Text>
            <Text size="sm" weight={600}>Prix du Canada {registration.competition_year}</Text>
          </Group>

          {!usesCart(seasonYear) && (
            <Group justify="space-between">
              <Text size="sm" color="dimmed" style={{ textAlign: 'left' }}>Payment Status:</Text>
              <Badge
                color={registration.payment_status === 'paid' ? 'green' : 'yellow'}
                variant="light"
              >
                {registration.payment_status === 'paid' ? 'Paid' : 'Pending'}
              </Badge>
            </Group>
          )}
        </Stack>

        {/* Cart seasons: what's registered and what's paid, entry by entry */}
        {usesCart(seasonYear) && (
          <div style={{ marginBottom: '1.5rem' }}>
            <Text size="sm" color="dimmed" mb="xs" style={{ textAlign: 'left' }}>Your entries</Text>
            <EntriesSummary registration={registration} />
          </div>
        )}

        <Group>
          {/* Cart seasons: entries are added and paid for after registering, so a paid
              dancer gets a direct way back to Step 3 (and checkout from there). */}
          {seasonOpen && usesCart(seasonYear) && (
            <Button
              flex={1}
              color="gold"
              leftSection={<IconPlus size={16} />}
              onClick={() => navigate('/register/individual/step-3')}
            >
              Add or Edit Entries
            </Button>
          )}
          <Button
            flex={1}
            variant={seasonOpen ? 'outline' : 'light'}
            leftSection={seasonOpen ? <IconEdit size={16} /> : <IconEye size={16} />}
            onClick={() => navigate('/register/individual/step-1')}
          >
            {seasonOpen ? (usesCart(seasonYear) ? 'Edit My Details' : 'Edit Registration') : 'View My Details'}
          </Button>
          <Button
            variant="outline"
            leftSection={<IconMail size={16} />}
            onClick={handleResendEmail}
            loading={sendingEmail}
          >
            Resend Confirmation
          </Button>
        </Group>
        {!seasonOpen && (
          <Text size="xs" color="dimmed" mt="md" style={{ textAlign: 'center' }}>
            Registration for {seasonYear} is closed. Need to make a change? Email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--primary-gold)', fontWeight: 600 }}>
              {CONTACT_EMAIL}
            </a>
          </Text>
        )}
      </Paper>
    );
  }

  return null;
};

export default RegistrationCard;
